import { AgTechClickEventHandler, AgTechButtonProps, AgTechButton } from "@agtech/web/components/Buttons/AgTechButtons";
import { AgTechFormSection } from "@agtech/web/components/Forms/Layout/AgTechFormSection";
import { buildGridEntitySelectionIndex, markGridSelectionEntityAsSelected, markGridSelectionEntityAsUnselected } from "@agtech/web/components/Grids/AgTechGridEntities";
import { useLotDataWriteLotInvoiceChangesAction, ChangeItemOnInvoiceDTO } from "@sales/data/lots/invoices/LotInvoiceDataActions";
import { InvoiceDataGetInvoiceLotsQuery } from "@sales/data/lots/invoices/LotInvoiceDataQueries";
import InvoiceChangeLotsGrid from "../../components/grids/InvoiceChangeLotsGrid";
import { useAgTechQuery, useAgTechQueryCombination, useAgTechQueryResultCombination } from "@agtech/core/data/queries/AgTechDataQueries";
import { useCurrentSale } from "@sales/data/sales/SaleDataStore";
import AgTechFormDialog from "@agtech/web/components/Portals/Dialogs/AgTechFormDialog";
import { useInvoiceDataGetInvoiceDetailsQuery } from "@sales/data/invoicing/InvoicingDataQueries";
import AgTechFormBody from "@agtech/web/components/Forms/Layout/AgTechFormBody";
import { getContactFullName } from "@agtech/data/contacts/ContactData";
import { AgTechFormReadOnlyTextControl } from "@agtech/web/components/Forms/Controls/AgTechFormReadOnlyTextControl";
import { AgTechFormRow } from "@agtech/web/components/Forms/Layout/AgTechFormRow";
import ContactDialog from "@agtech/web/integrations/contacts/dialogs/ContactDialog";
import { AgTechMoneyFormatter } from "@agtech/web/utilities/AgTechUtilities";
import SaleContactDetailPopUp from "../../../contacts/popups/detail/SaleContactDetailPopUp";
import { LotDataGetLotsQuery } from "@sales/data/lots/LotDataQueries";

export type InvoiceChangeLotsDialogProps = {
    invoiceId: number,
    contactId: number
}

const InvoiceChangeLotsDialog = (props: InvoiceChangeLotsDialogProps) => {
    const dialogQuery = useInvoiceChangeLotsDialogQuery(props);
    const dialogAction = useLotDataWriteLotInvoiceChangesAction(props);

    return (
        <AgTechFormDialog
            dialogData={{
                query: dialogQuery,
                getDialogData: d => {
                    return buildGridEntitySelectionIndex({
                        entities: d.lots,
                        isSelected: l => l.invoiceId === props.invoiceId
                    });
                },
                getDialogContext: (d) => {
                    return {
                        invoice: d.invoiceDetails.invoice,
                        contact: d.invoiceDetails.contact,
                        lots: d.invoiceDetails.lots,
                        invoicedLots: d.invoiceDetails.invoice.details.lotsPurchased,
                    }
                }
            }}
            dialogAction={{
                action: dialogAction,
                getActionData: d => {
                    const lotSelections = Object.values(d);
                    const updatedLots = lotSelections.filter(d => d.isSelected != d.wasOriginallySelected);

                    return updatedLots.map<ChangeItemOnInvoiceDTO>(l => ({
                        remove: l.wasOriginallySelected == true && l.isSelected == false,
                        id: l.entity.id
                    }));
                }
            }}
            structure={(data) => ({
                header: `Update Lots - Invoice #${data.context.invoice.info.number}`,
            })}
            width={600}
         >
            {(formContext) => {
                const selectedLots = Object.values(formContext.entity).filter(l => l.isSelected);

                const creditsTotal = formContext.context.invoice.details.creditsApplied.reduce((acc, curr) => acc + (curr.creditAmount ?? 0), 0);
                const discountsTotal = formContext.context.invoice.details.discountsGiven.reduce((acc, curr) => acc + curr.amount, 0);
            
                const updatedLotTotal = selectedLots
                    .reduce((acc, curr) => acc + (formContext.context.lots[curr.id]?.purchasePrice ?? 0), 0);

                const invoiceTotal = updatedLotTotal - discountsTotal - creditsTotal;

                return (
                    <AgTechFormBody>
                        <AgTechFormRow>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption="Contact Name"
                                value={e => getContactFullName(formContext.context.contact.info)}
                                onClick={e => ContactDialog.Open(e, {
                                    contactId: formContext.context.contact.id
                                })}
                                showPopUpOnHover={e => ({
                                    popup: () => <SaleContactDetailPopUp contactId={formContext.context.contact.id} />
                                })}
                            />
                        </AgTechFormRow>
                        <AgTechFormRow>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption='Lots sold'
                                value={e => AgTechMoneyFormatter.format(updatedLotTotal)}
                                classes='snug'
                            />
                            <h1 className='pt-4 font-semibold text-lg tall centered flex'>-</h1>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption='Discounts Given'
                                value={e => AgTechMoneyFormatter.format(discountsTotal)}
                                classes='snug'
                            />
                            <h1 className='pt-4 font-semibold text-lg tall centered flex'>-</h1>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption='Credits Applied'
                                value={e => AgTechMoneyFormatter.format(creditsTotal)}
                                classes='snug'
                            />
                            <h1 className='pt-4 font-semibold text-lg tall centered flex'>=</h1>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption='Invoice Amount'
                                value={e => AgTechMoneyFormatter.format(invoiceTotal)}
                                classes='snug'
                            />
                        </AgTechFormRow>
                        <AgTechFormSection
                            header="Lots"
                            classes='flexed'
                            bodyClasses='flexed pt-3'
                        >
                            <InvoiceChangeLotsGrid
                                contactId={formContext.context.contact.id}
                                availableLots={formContext.entity}
                                onLotSelected={async (lot) => {
                                    formContext.updateFormEntity(curr => {
                                        return markGridSelectionEntityAsSelected({
                                            selections: curr,
                                            entity: {
                                                ...lot,
                                                invoiceId: props.invoiceId,
                                                invoiceState: 0
                                            }
                                        });
                                    });
                                }}
                                onLotDeSelected={async lot => {
                                    formContext.updateFormEntity(curr => {
                                        return markGridSelectionEntityAsUnselected({
                                            selections: curr,
                                            entity: {
                                                ...lot,
                                                invoiceId: props.invoiceId,
                                                invoiceState: 0
                                            }
                                        });
                                    });
                                }}
                            />
                        </AgTechFormSection>
                    </AgTechFormBody>
                )
            }}
        </AgTechFormDialog>
    )
}

const useInvoiceChangeLotsDialogQuery = (props: InvoiceChangeLotsDialogProps) => {
    const currentSale = useCurrentSale();

    const invoiceDetailsQuery = useInvoiceDataGetInvoiceDetailsQuery({
        invoiceId: props.invoiceId
    });

    const contactLotsQuery = useAgTechQuery({
        query: InvoiceDataGetInvoiceLotsQuery,
        queryContext: {
            saleId: currentSale.id,
            invoiceId: props.invoiceId,
            contactId: props.contactId
        }
    });

    const lotDataQuery = useAgTechQuery({
        query: LotDataGetLotsQuery,
        queryContext: {
            saleId: currentSale.id
        }
    });

    const lotInvoiceDataQuery = useAgTechQueryResultCombination({
        firstQuery: invoiceDetailsQuery,
        secondQuery: contactLotsQuery,
        projection: (invoiceDetails, contactLots) => ({
            invoiceDetails,
            lots: contactLots
        })
    });

    return useAgTechQueryResultCombination({
        firstQuery: lotInvoiceDataQuery,
        secondQuery: lotDataQuery,
        projection: (invoiceData, lots) => ({
            invoiceDetails: invoiceData.invoiceDetails,
            lots: invoiceData.lots.map(l => ({
                ...l,
                ...lots[l.id]
            }))
        })
    })
}

InvoiceChangeLotsDialog.Open = (e: AgTechClickEventHandler, props: InvoiceChangeLotsDialogProps) => {
    return e.dialogs.openDialog({
        dialog: () => {
            return (
                <InvoiceChangeLotsDialog {...props} />
            )
        }
    });
}

InvoiceChangeLotsDialog.Button = (props: AgTechButtonProps & InvoiceChangeLotsDialogProps) => {
    let buttonProps: AgTechButtonProps = {
        text: 'Change Lots',
        icon: 'bi bi-arrow-left-right',
        ...props,
        action: e => InvoiceChangeLotsDialog.Open(e, props)
    }

    return <AgTechButton {...buttonProps} />
}

export default InvoiceChangeLotsDialog;